import React, { useState, useRef, useEffect } from 'react';
import './SpeechPractice.css';
import axios from 'axios';
import { API_URL } from '../config';

function SpeechPractice({ onNavigateToMetrics }) {
  const [currentTopic, setCurrentTopic] = useState(null);
  const [recommendedTopics, setRecommendedTopics] = useState([]);
  const [practicedTopics, setPracticedTopics] = useState(new Set()); // IDs dos tópicos já praticados
  const [isRecording, setIsRecording] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoadingTopics, setIsLoadingTopics] = useState(true);
  const [allTopicsCompleted, setAllTopicsCompleted] = useState(false);
  const [improvementPoints, setImprovementPoints] = useState(null);
  const [isLoadingProgress, setIsLoadingProgress] = useState(false);
  const [microphonePermission, setMicrophonePermission] = useState(null); // null = não verificado, true = concedido, false = negado
  const [isRequestingPermission, setIsRequestingPermission] = useState(false);
  
  const recognitionRef = useRef(null);
  const synthRef = useRef(null);
  const recommendedTopicsRef = useRef([]);
  
  // Manter referência atualizada dos tópicos recomendados
  useEffect(() => {
    recommendedTopicsRef.current = recommendedTopics;
  }, [recommendedTopics]);
  
  // Buscar pontos de melhoria do backend
  const loadImprovementPoints = async () => {
    try {
      setIsLoadingProgress(true);
      const response = await axios.get(`${API_URL}/v1/progress`);
      const { errorCounts, mostRecurrentError } = response.data;
      
      const points = [];
      if (errorCounts.tense > 0) {
        points.push({
          type: 'tense',
          label: 'Tempos Verbais',
          count: errorCounts.tense,
          description: 'Preste atenção aos tempos verbais (presente, passado, futuro)',
        });
      }
      if (errorCounts.structure > 0) {
        points.push({
          type: 'structure',
          label: 'Estrutura da Frase',
          count: errorCounts.structure,
          description: 'Revise a ordem das palavras e estrutura gramatical',
        });
      }
      if (errorCounts.pronunciation > 0) {
        points.push({
          type: 'pronunciation',
          label: 'Pronúncia',
          count: errorCounts.pronunciation,
          description: 'Pratique a pronúncia das palavras',
        });
      }
      
      setImprovementPoints({
        points,
        mostRecurrentError,
        totalErrors: errorCounts.tense + errorCounts.structure + errorCounts.pronunciation,
      });
    } catch (error) {
      console.error('Erro ao carregar pontos de melhoria:', error);
      setImprovementPoints({ points: [], mostRecurrentError: null, totalErrors: 0 });
    } finally {
      setIsLoadingProgress(false);
    }
  };


  // Inicializar Web Speech API quando o tópico mudar
  useEffect(() => {
    if (!currentTopic) return;

    // Inicializar Web Speech API
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = false;
      recognitionRef.current.interimResults = false;
      recognitionRef.current.lang = 'en-US';

      recognitionRef.current.onresult = (event) => {
        const spokenText = event.results[0][0].transcript;
        handleSpeechResult(spokenText);
      };

      recognitionRef.current.onerror = (event) => {
        console.error('Erro no reconhecimento de fala:', event.error);
        setFeedback('Erro ao reconhecer fala. Tente novamente.');
        setIsRecording(false);
      };

      recognitionRef.current.onend = () => {
        setIsRecording(false);
      };
    } else {
      console.warn('Web Speech API não suportada neste navegador');
    }

    // Inicializar Speech Synthesis
    if ('speechSynthesis' in window) {
      synthRef.current = window.speechSynthesis;
    }

    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTopic]);

  const loadRecommendedTopics = async () => {
    try {
      setIsLoadingTopics(true);
      setAllTopicsCompleted(false);
      
      // Carregar tópicos recomendados (15 tópicos baseados em métricas)
      const recommendedResponse = await axios.get(`${API_URL}/v1/topics/recommended`);
      const recommended = recommendedResponse.data || [];
      
      setRecommendedTopics(recommended);
      setPracticedTopics(new Set());
      
      // Se houver tópicos recomendados, usar o primeiro
      if (recommended.length > 0) {
        setCurrentTopic(recommended[0]);
      } else {
        // Fallback: usar tópico aleatório
        const randomResponse = await axios.get(`${API_URL}/v1/topics/random`);
        setCurrentTopic(randomResponse.data);
      }
    } catch (error) {
      console.error('Erro ao carregar tópicos recomendados:', error);
      setFeedback('Erro ao carregar tópicos. Tente recarregar a página.');
    } finally {
      setIsLoadingTopics(false);
    }
  };

  // Verificar permissão do microfone na inicialização
  useEffect(() => {
    const checkMicrophonePermission = async () => {
      try {
        if (navigator.permissions) {
          const result = await navigator.permissions.query({ name: 'microphone' });
          setMicrophonePermission(result.state === 'granted');
          
          // Listener para mudanças na permissão
          result.onchange = () => {
            setMicrophonePermission(result.state === 'granted');
          };
        } else {
          // Se a API de permissões não estiver disponível, tenta verificar de outra forma
          setMicrophonePermission(null);
        }
      } catch (error) {
        console.log('Não foi possível verificar permissão do microfone:', error);
        setMicrophonePermission(null);
      }
    };

    checkMicrophonePermission();
  }, []);

  // Carregar tópicos recomendados na inicialização
  useEffect(() => {
    loadRecommendedTopics();
  }, []);

  // Verificar se todos os tópicos recomendados foram praticados
  useEffect(() => {
    if (recommendedTopics.length > 0 && practicedTopics.size >= recommendedTopics.length) {
      setAllTopicsCompleted(true);
    }
  }, [practicedTopics, recommendedTopics]);


  const playSentence = () => {
    if (!synthRef.current || !currentTopic) {
      setFeedback('Seu navegador não suporta reprodução de áudio.');
      return;
    }

    setIsPlaying(true);
    setFeedback('');

    // Cancelar qualquer fala anterior
    synthRef.current.cancel();

    const utterance = new SpeechSynthesisUtterance(currentTopic.sentence);
    utterance.lang = 'en-US';
    utterance.rate = 0.9; // Velocidade um pouco mais lenta
    utterance.pitch = 1;
    utterance.volume = 1;

    utterance.onend = () => {
      setIsPlaying(false);
    };

    utterance.onerror = () => {
      setIsPlaying(false);
      setFeedback('Erro ao reproduzir áudio.');
    };

    synthRef.current.speak(utterance);
  };

  const requestMicrophonePermission = async () => {
    setIsRequestingPermission(true);
    try {
      // Solicitar acesso ao microfone usando getUserMedia
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      // Se chegou aqui, a permissão foi concedida
      setMicrophonePermission(true);
      // Parar o stream imediatamente, só precisávamos da permissão
      stream.getTracks().forEach(track => track.stop());
      setFeedback('Permissão concedida! Agora você pode gravar.');
      return true;
    } catch (error) {
      console.error('Erro ao solicitar permissão do microfone:', error);
      setMicrophonePermission(false);
      if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
        setFeedback('Permissão do microfone negada. Por favor, permita o acesso nas configurações do navegador.');
      } else {
        setFeedback('Erro ao acessar o microfone. Verifique as configurações do navegador.');
      }
      return false;
    } finally {
      setIsRequestingPermission(false);
    }
  };

  const startRecording = async () => {
    if (!recognitionRef.current) {
      setFeedback('Reconhecimento de fala não disponível no seu navegador.');
      return;
    }

    // Se a permissão não foi verificada ou foi negada, solicitar
    if (microphonePermission === false || microphonePermission === null) {
      const permissionGranted = await requestMicrophonePermission();
      // Se ainda não tiver permissão após solicitar, não continua
      if (!permissionGranted) {
        return;
      }
    }

    setFeedback('');
    setIsRecording(true);
    
    try {
      recognitionRef.current.start();
    } catch (error) {
      console.error('Erro ao iniciar gravação:', error);
      
      // Se o erro for de permissão, tentar solicitar novamente
      if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
        setMicrophonePermission(false);
        setFeedback('Permissão do microfone necessária. Clique novamente para permitir.');
      } else {
        setFeedback('Erro ao iniciar gravação. Tente novamente.');
      }
      setIsRecording(false);
    }
  };

  const stopRecording = () => {
    if (recognitionRef.current && isRecording) {
      recognitionRef.current.stop();
      setIsRecording(false);
    }
  };

  const handleSpeechResult = async (spokenText) => {
    if (!currentTopic) return;

    setIsLoading(true);
    setFeedback('Processando...');

    try {
      const response = await axios.post(`${API_URL}/v1/speech/compare`, {
        expected: currentTopic.sentence,
        spoken: spokenText,
        topicId: currentTopic.id,
        focus: currentTopic.focus,
      });

      const { isCorrect, feedback: feedbackMessage } = response.data;
      setFeedback(feedbackMessage);

      // Marcar tópico atual como praticado (independente de acertar ou errar)
      const currentTopicId = currentTopic.id;
      
      // Atualizar o estado de praticados
      setPracticedTopics(prev => {
        const newSet = new Set([...prev, currentTopicId]);
        
        // Se completou todos os tópicos, buscar pontos de melhoria
        if (newSet.size >= recommendedTopicsRef.current.length) {
          loadImprovementPoints();
        }
        
        return newSet;
      });
      
      // Encontrar próximo tópico recomendado que ainda não foi praticado
      // Usar ref para garantir que temos os tópicos mais recentes
      const delay = isCorrect ? 2000 : 3000;
      setTimeout(() => {
        setPracticedTopics(currentPracticed => {
          // Se completou todos os tópicos
          if (currentPracticed.size >= recommendedTopicsRef.current.length) {
            setAllTopicsCompleted(true);
            setCurrentTopic(null);
            return currentPracticed;
          }
          
          const nextTopicToLoad = recommendedTopicsRef.current.find(
            (topic) => !currentPracticed.has(topic.id)
          );
          
          if (nextTopicToLoad) {
            setCurrentTopic(nextTopicToLoad);
            setFeedback('');
          } else {
            // Todos os tópicos foram praticados
            setAllTopicsCompleted(true);
            setCurrentTopic(null);
          }
          
          return currentPracticed;
        });
      }, delay);
    } catch (error) {
      console.error('Erro ao comparar fala:', error);
      setFeedback('Erro ao processar. Tente novamente.');
    } finally {
      setIsLoading(false);
    }
  };

  const nextSentence = async () => {
    if (recommendedTopics.length === 0 || allTopicsCompleted || !currentTopic) return;
    
    const currentTopicId = currentTopic.id;
    
    // Marcar tópico atual como pulado no backend
    try {
      await axios.post(`${API_URL}/v1/metrics/skip`, {
        topicId: currentTopicId,
      });
    } catch (error) {
      console.error('Erro ao marcar tópico como pulado:', error);
      // Continua mesmo se houver erro
    }
    
    // Marcar como praticado no frontend e encontrar próximo tópico
    setPracticedTopics(prev => {
      const newSet = new Set([...prev, currentTopicId]);
      
      // Encontrar próximo tópico que ainda não foi praticado
      const nextTopicToLoad = recommendedTopicsRef.current.find(
        (topic) => !newSet.has(topic.id)
      );
      
      if (nextTopicToLoad) {
        setCurrentTopic(nextTopicToLoad);
        setFeedback('');
      } else {
        // Todos os tópicos foram praticados
        loadImprovementPoints();
        setAllTopicsCompleted(true);
        setCurrentTopic(null);
      }
      
      return newSet;
    });
  };

  const prevSentence = () => {
    if (recommendedTopics.length === 0 || allTopicsCompleted) return;
    const currentIndex = recommendedTopics.findIndex(t => t.id === currentTopic?.id);
    const prevIndex = currentIndex <= 0
      ? recommendedTopics.length - 1
      : currentIndex - 1;
    setCurrentTopic(recommendedTopics[prevIndex]);
    setFeedback('');
  };
  
  // Verificar se está no último tópico
  const isLastTopic = currentTopic && recommendedTopics.length > 0 && 
    recommendedTopics.findIndex(t => t.id === currentTopic.id) === recommendedTopics.length - 1;

  const startNewRound = () => {
    setAllTopicsCompleted(false);
    setPracticedTopics(new Set());
    setImprovementPoints(null);
    setCurrentTopic(null);
    setFeedback('');
    loadRecommendedTopics();
  };

  if (isLoadingTopics) {
    return (
      <div className="speech-practice">
        <div className="practice-card">
          <h1 className="title">Easy English</h1>
          <p className="subtitle">Carregando tópicos...</p>
        </div>
      </div>
    );
  }

  if (allTopicsCompleted) {
    return (
      <div className="speech-practice">
        <div className="practice-card">
          <div className="completion-message">
            <h1 className="title">🎉 Parabéns!</h1>
            <p className="subtitle">Você completou todos os {recommendedTopics.length} tópicos recomendados!</p>
            
            {isLoadingProgress ? (
              <div className="completion-stats">
                <p>Carregando seus pontos de melhoria...</p>
              </div>
            ) : improvementPoints && improvementPoints.totalErrors > 0 ? (
              <div className="completion-stats">
                <h3>📈 Pontos para Melhorar:</h3>
                <div className="improvement-points">
                  {improvementPoints.points.map((point, index) => (
                    <div key={index} className="improvement-point">
                      <div className="improvement-header">
                        <span className="improvement-label">{point.label}</span>
                        <span className="improvement-count">{point.count} erro{point.count !== 1 ? 's' : ''}</span>
                      </div>
                      <p className="improvement-description">{point.description}</p>
                    </div>
                  ))}
                  {improvementPoints.mostRecurrentError && (
                    <div className="most-recurrent-error">
                      <strong>Foco principal:</strong> {improvementPoints.mostRecurrentError === 'tense' ? 'Tempos Verbais' : 
                        improvementPoints.mostRecurrentError === 'structure' ? 'Estrutura da Frase' : 'Pronúncia'}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="completion-stats">
                <p>✨ Excelente! Você não teve erros nesta rodada!</p>
              </div>
            )}
            
            <div className="completion-actions">
              <button className="btn btn-primary" onClick={startNewRound}>
                🔄 Nova Rodada
              </button>
              {onNavigateToMetrics && (
                <button className="btn btn-secondary" onClick={onNavigateToMetrics}>
                  📊 Ver Métricas Detalhadas
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!currentTopic) {
    return (
      <div className="speech-practice">
        <div className="practice-card">
          <h1 className="title">Easy English</h1>
          <p className="subtitle">Erro ao carregar tópico</p>
        </div>
      </div>
    );
  }

  return (
    <div className="speech-practice">
      <div className="practice-card">
        <div className="practice-header">
          <div>
            <h1 className="title">Easy English</h1>
            <p className="subtitle">Treine sua pronúncia</p>
          </div>
          <div className="header-actions">
            {onNavigateToMetrics && (
              <button 
                className="btn btn-metrics" 
                onClick={onNavigateToMetrics}
              >
                📊 Ver Métricas
              </button>
            )}
          </div>
        </div>

        {currentTopic.focus && (
          <div className="focus-badge">
            Foco: {currentTopic.focus.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
          </div>
        )}
        {currentTopic.situation && (
          <div className="situation-badge">
            {currentTopic.situation}
          </div>
        )}

        {/* Progresso dos tópicos recomendados */}
        {recommendedTopics.length > 0 && (
          <div className="progress-indicator">
            <div className="progress-text">
              Tópico {practicedTopics.size + 1} de {recommendedTopics.length}
            </div>
            <div className="progress-bar">
              <div 
                className="progress-fill" 
                style={{ width: `${((practicedTopics.size + 1) / recommendedTopics.length) * 100}%` }}
              ></div>
            </div>
          </div>
        )}

        <div className="sentence-container">
          <div className="sentence-display">
            <p className="sentence-text">{currentTopic.sentence}</p>
          </div>

          <div className="sentence-controls">
            <button 
              className="btn btn-secondary" 
              onClick={prevSentence}
              disabled={isRecording || isPlaying}
            >
              ← Anterior
            </button>
            <button 
              className="btn btn-primary" 
              onClick={playSentence}
              disabled={isRecording || isPlaying}
            >
              {isPlaying ? '▶ Reproduzindo...' : '🔊 Ouvir frase'}
            </button>
            <button 
              className="btn btn-secondary"
              onClick={nextSentence}
              disabled={isRecording || isPlaying || allTopicsCompleted || isLastTopic}
            >
              Próxima →
            </button>
          </div>
        </div>

        <div className="recording-section">
          {microphonePermission === false && (
            <div className="permission-warning">
              <p>⚠️ Permissão do microfone necessária para gravar</p>
              <button
                className="btn btn-primary"
                onClick={requestMicrophonePermission}
                disabled={isRequestingPermission}
              >
                {isRequestingPermission ? 'Solicitando...' : '🔓 Permitir Microfone'}
              </button>
            </div>
          )}
          
          <button
            className={`btn btn-record ${isRecording ? 'recording' : ''}`}
            onClick={isRecording ? stopRecording : startRecording}
            disabled={isLoading || isPlaying || isRequestingPermission}
          >
            {isRecording ? '⏹ Parar gravação' : '🎤 Falar frase'}
          </button>

          {isLoading && (
            <div className="loading">Processando sua fala...</div>
          )}

          {feedback && (
            <div className={`feedback ${feedback.includes('Nice') || feedback.includes('🎉') ? 'success' : ''}`}>
              {feedback}
            </div>
          )}
        </div>

        <div className="instructions">
          <p>1. Clique em "Ouvir frase" para escutar</p>
          <p>2. Clique em "Falar frase" e repita o que ouviu</p>
          <p>3. Receba feedback e continue praticando!</p>
        </div>
      </div>
    </div>
  );
}

export default SpeechPractice;

